/**
 * `GeometrySnapshot` ↔ JSON serializer.
 *
 * Mirrors `scene-serde.ts`: `toJson` stringifies the frozen artifact,
 * `fromJson` validates the untrusted-JSON shape at the boundary (throwing with
 * context on malformed input, per the SDK error-boundary convention) and
 * reconstructs a `GeometrySnapshot` in the same canonical key order `build`
 * emits — so the round-trip is **byte-stable**: `toJson(fromJson(toJson(g)))`
 * is identical to `toJson(g)`.
 *
 * The snapshot is a flowing message of plain frozen objects + ordered arrays
 * (no `Map`/`Set`/class instances), so serialization is a direct
 * `JSON.stringify` with no adapter — the geometry analog of the scene serde
 * that hardens every §2 artifact against the wire/persistence boundary.
 */

import type { Option } from "../core/option.ts";
import { Json } from "../core/json.ts";
import { ArtifactFreeze } from "@tento-core/artifact-freeze";
import type { JsonReader, JsonValue } from "@tento-core/json-reader";

import { GeometrySnapshot } from "./geometry-snapshot.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import type { RowSection } from "./scene.ts";

const SCHEMA_VERSION = 1;

/**
 * Serializes a `GeometrySnapshot` to canonical JSON and validates it back, byte-stable, for
 * snapshot tests and debugging tools.
 */
export namespace GeometrySnapshotSerde {
  /** Serialize a snapshot to its canonical JSON string. */
  export function toJson(g: GeometrySnapshot): string {
    return JSON.stringify(g);
  }

  /** Parse + validate a snapshot from untrusted JSON. Throws with context on malformed input. */
  export function fromJson(s: string): GeometrySnapshot {
    let parsed: JsonValue;
    try {
      parsed = Json.wire(JSON.parse(s)) ?? null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return fail(`invalid JSON: ${msg}`);
    }
    const root = obj(parsed, "root");

    const version = num(field(root, "schemaVersion"), "schemaVersion");
    if (version !== SCHEMA_VERSION) {
      fail(`unsupported schemaVersion ${version} (expected ${SCHEMA_VERSION})`);
    }

    const snapshot: GeometrySnapshot = {
      schemaVersion: SCHEMA_VERSION,
      identity: decodeIdentity(obj(field(root, "identity"), "identity")),
      revision: decodeRevision(obj(field(root, "revision"), "revision")),
      vertical: decodeVertical(obj(field(root, "vertical"), "vertical")),
      rows: arr(field(root, "rows"), "rows").map((r, i) => decodeRow(obj(r, `rows[${i}]`), `rows[${i}]`)),
      columns: arr(field(root, "columns"), "columns").map((c, i) =>
        decodeColumn(obj(c, `columns[${i}]`), `columns[${i}]`)
      ),
      window: decodeWindow(obj(field(root, "window"), "window")),
      work: decodeWork(obj(field(root, "work"), "work")),
    };

    return ArtifactFreeze.deep(snapshot);
  }

  // ── Field decoders (canonical key order matches `GeometrySnapshot.build`) ──

  function decodeIdentity(o: JsonReader.Object): GeometrySnapshot.Identity {
    return {
      sheetKey: SheetNetworkKey.fromWire(str(field(o, "sheetKey"), "identity.sheetKey")),
      timescale: str(field(o, "timescale"), "identity.timescale"),
    };
  }

  function decodeRevision(o: JsonReader.Object): GeometrySnapshot.Revision {
    return {
      tau: num(field(o, "tau"), "revision.tau"),
      epoch: num(field(o, "epoch"), "revision.epoch"),
    };
  }

  function decodeVertical(o: JsonReader.Object): GeometrySnapshot.VerticalBands {
    return {
      headerHeightPx: num(field(o, "headerHeightPx"), "vertical.headerHeightPx"),
      bodyHeightPx: num(field(o, "bodyHeightPx"), "vertical.bodyHeightPx"),
      footerHeightPx: num(field(o, "footerHeightPx"), "vertical.footerHeightPx"),
      totalHeightPx: num(field(o, "totalHeightPx"), "vertical.totalHeightPx"),
      initialScrollTopPx: num(field(o, "initialScrollTopPx"), "vertical.initialScrollTopPx"),
      frozenRowCount: num(field(o, "frozenRowCount"), "vertical.frozenRowCount"),
      frozenFlattenedRowCount: num(field(o, "frozenFlattenedRowCount"), "vertical.frozenFlattenedRowCount"),
      footerRowCount: num(field(o, "footerRowCount"), "vertical.footerRowCount"),
      footerFlattenedRowCount: num(field(o, "footerFlattenedRowCount"), "vertical.footerFlattenedRowCount"),
    };
  }

  function decodeRow(o: JsonReader.Object, ctx: string): GeometrySnapshot.RowGeometry {
    return {
      rowId: RowNetworkKey.fromWire(str(field(o, "rowId"), `${ctx}.rowId`)),
      rowIndex: num(field(o, "rowIndex"), `${ctx}.rowIndex`),
      section: section(field(o, "section"), `${ctx}.section`),
      offsetPx: num(field(o, "offsetPx"), `${ctx}.offsetPx`),
      cellHeightPx: num(field(o, "cellHeightPx"), `${ctx}.cellHeightPx`),
      cellHeightWithChildrenPx: num(field(o, "cellHeightWithChildrenPx"), `${ctx}.cellHeightWithChildrenPx`),
      stacking: o.stacking == null
        ? null
        : decodeStacking(obj(field(o, "stacking"), `${ctx}.stacking`), `${ctx}.stacking`),
    };
  }

  function decodeStacking(o: JsonReader.Object, ctx: string): GeometrySnapshot.RowStacking {
    return {
      cellsPerColumn: num(field(o, "cellsPerColumn"), `${ctx}.cellsPerColumn`),
      individualCellHeightPx: num(field(o, "individualCellHeightPx"), `${ctx}.individualCellHeightPx`),
      visibleCellCount: num(field(o, "visibleCellCount"), `${ctx}.visibleCellCount`),
      seeMoreHeightPx: num(field(o, "seeMoreHeightPx"), `${ctx}.seeMoreHeightPx`),
    };
  }

  function decodeColumn(o: JsonReader.Object, ctx: string): GeometrySnapshot.ColumnCell {
    return {
      offset: num(field(o, "offset"), `${ctx}.offset`),
      leftPx: num(field(o, "leftPx"), `${ctx}.leftPx`),
      widthPx: num(field(o, "widthPx"), `${ctx}.widthPx`),
    };
  }

  function decodeWindow(o: JsonReader.Object): GeometrySnapshot.ScrollWindow {
    return {
      viewportWidthPx: num(field(o, "viewportWidthPx"), "window.viewportWidthPx"),
      contentWidthPx: num(field(o, "contentWidthPx"), "window.contentWidthPx"),
      firstColumnOffset: num(field(o, "firstColumnOffset"), "window.firstColumnOffset"),
      lastColumnOffsetExclusive: num(field(o, "lastColumnOffsetExclusive"), "window.lastColumnOffsetExclusive"),
    };
  }

  function decodeWork(o: JsonReader.Object): GeometrySnapshot.BuildCounters {
    return {
      rowLayoutsRead: num(field(o, "rowLayoutsRead"), "work.rowLayoutsRead"),
      rowsEmitted: num(field(o, "rowsEmitted"), "work.rowsEmitted"),
      rowsReused: num(field(o, "rowsReused"), "work.rowsReused"),
      columnsReused: num(field(o, "columnsReused"), "work.columnsReused"),
    };
  }

  // ── Primitive validators ─────────────────────────────────────────────────

  function fail(msg: string): never {
    throw new Error(`geometry-snapshot-serde: ${msg}`);
  }

  /**
   * `o`'s `key`: its value, or `null` when the key is absent — a decoder
   * then refuses it with the field's own context (absent and `null` are the
   * same missing field here).
   */
  function field(o: JsonReader.Object, key: string): Option<JsonValue> {
    return o[key] ?? null;
  }

  function isJsonArray(v: JsonValue): v is readonly JsonValue[] {
    return Array.isArray(v);
  }

  function obj(v: Option<JsonValue>, ctx: string): JsonReader.Object {
    if (v == null || typeof v !== "object" || isJsonArray(v)) {
      return fail(`${ctx} must be an object`);
    }
    return v;
  }

  function arr(v: Option<JsonValue>, ctx: string): readonly JsonValue[] {
    if (v == null || !isJsonArray(v)) return fail(`${ctx} must be an array`);
    return v;
  }

  function num(v: Option<JsonValue>, ctx: string): number {
    if (typeof v !== "number" || !Number.isFinite(v)) fail(`${ctx} must be a finite number`);
    return v;
  }

  function str(v: Option<JsonValue>, ctx: string): string {
    if (typeof v !== "string") fail(`${ctx} must be a string`);
    return v;
  }

  function section(v: Option<JsonValue>, ctx: string): RowSection {
    const s = str(v, ctx);
    if (s !== "header" && s !== "body" && s !== "footer") {
      fail(`${ctx} must be one of header|body|footer (got "${s}")`);
    }
    return s;
  }
}
